import type { QueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch } from "../api/client";
import type { Answers } from "./answers";
import type { Selection } from "./articles";
import { selectedDaysOf } from "./dates";
import type { Recorded } from "./participant";

import { formatFullName } from "./nomAffiche";
/**
 * LES ACCOMPAGNANTS — les personnes qu'un participant a inscrites avec lui.
 *
 *   GET /domains/:d/events/:e/companions?userId=…   api-v1
 *
 * ⚠️ SANS LE PRÉFIXE `/v2/`, ET CE N'EST PAS UN OUBLI. Les handlers vivent pourtant
 * dans `routesV2/companions/`, mais leur routeur n'est monté QUE par le routeur de
 * première génération (`api/routes/event.routes.js`). Sous `/v2/…`, la même adresse
 * répond « Oops, cette route n'existe pas » — vérifié. C'est aussi celle que la V1
 * appelle.
 *
 * ⚠️ CE QUI FAIT UN ACCOMPAGNANT : `UserEvents.registeredBy2` pointe sur le porteur.
 * C'est un VRAI compte, avec sa propre participation et son propre badge — pas une
 * ligne de texte sur l'inscription de quelqu'un d'autre. D'où le nom au singulier de
 * la table : ce sont des gens, et c'est pour cela que le récapitulatif leur donne une
 * section plutôt qu'une ligne.
 *
 * ⚠️ ET LE PORTEUR PEUT FIGURER DANS SA PROPRE LISTE. La requête du serveur retient
 * `registeredBy2 = userId`, mais la V1 filtre malgré tout son propre identifiant à la
 * lecture (`users.filter(f => f.id !== userId)`) — un porteur qui se serait inscrit
 * lui-même se compterait deux fois, une fois comme participant et une fois comme son
 * propre accompagnant. On garde ce filet.
 */

/** Un accompagnant, réduit à ce que le récapitulatif en dit. */
export type Companion = {
  id: number;
  /**
   * « Prénom Nom », ou ce qu'il en reste.
   *
   * ⚠️ JAMAIS VIDE, et ce n'est pas de la coquetterie : c'est la VALEUR de sa ligne au
   * récapitulatif, et une valeur vide y fait disparaître la ligne entière — on se
   * retrouve avec un titre de section au-dessus du néant. Un accompagnant sans nom
   * existe (créé à la volée, importé à moitié), donc il se désigne par son rang.
   */
  name: string;
  /** Le même nom au format d'AFFICHAGE « Prénom NOM » (liste seulement : `name` reste la valeur d'origine). */
  label: string;
  /**
   * THEIR BADGE CODE — `UserEvents.code`, theirs, not the holder's.
   *
   * ⚠️ A COMPANION IS A BADGE HOLDER IN THEIR OWN RIGHT: the route that registers them
   * sets their own `code` and their own `voucherCode`. Reusing the holder's to draw
   * their QR would let two people in on one registration, and the entrance count would
   * only ever see one.
   *
   * ⚠️ ABSENT UNTIL WE HAVE IT: no QR and no identifier on their card, rather than a
   * square that scans and names nobody.
   */
  code?: string;
};

type Raw = {
  id?: number;
  firstName?: string | null;
  lastName?: string | null;
  /* The route includes everybody's registration (`db.UserEvent`): the code is already
     in there, nothing more to ask for. */
  UserEvent?: { code?: string | null } | null;
};

/**
 * CE QUE LA ROUTE REND, RAMENÉ AU SEUL NOM.
 *
 * ⚠️ L'ADRESSE N'EST PAS REPRISE, bien que la route la porte (`Authentication.email`).
 * Le récapitulatif ne l'affiche pas — le prototype non plus — et beaucoup
 * d'accompagnants n'ont aucun compte : la lire pour ne rien en faire donnerait un champ
 * dont personne ne saurait dire s'il est vide par accident. L'écran qui les gérera la
 * relira le jour où il en aura besoin.
 *
 * Exporté pour son test : les noms arrivent parfois avec des espaces en trop (celui de
 * l'événement de test s'appelle « Tes » suivi de deux blancs), et une carte qui affiche
 * « Tes   » a l'air cassée.
 */
export function readCompanions(
  raw: unknown,
  bearer: number | undefined,
): Companion[] {
  const list = Array.isArray(raw) ? raw : [];
  return list.flatMap((u: Raw) => {
    if (typeof u?.id !== "number" || u.id === bearer) return [];
    const name = [u.firstName, u.lastName]
      .map((m) => (m ?? "").trim())
      .filter(Boolean)
      .join(" ");
    const code = (u.UserEvent?.code ?? "").trim();
    return [
      {
        id: u.id,
        // Sans nom, on le désigne par son rang plutôt que par un blanc : la ligne
        // existe, la personne aussi, et c'est au porteur de la reconnaître.
        name: name || `Accompagnant ${u.id}`,
        label: formatFullName(u.firstName, u.lastName) || name || `Accompagnant ${u.id}`,
        ...(code ? { code } : {}),
      },
    ];
  });
}

export async function fetchCompanions(
  domainId: number,
  eventId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<Companion[]> {
  const answer = await apiFetch<{ users?: unknown } | null>(
    `/domains/${domainId}/events/${eventId}/companions?userId=${userId}`,
    { signal },
  );
  return readCompanions(answer?.users, userId);
}

/**
 * INSCRIT UNE PERSONNE AVEC SOI — et rend l'identifiant qu'elle reçoit.
 *
 *   POST /domains/:d/events/:e/companions?userId=<porteur>
 *
 * ⚠️ CETTE ROUTE FAIT TOUT LE RATTACHEMENT, et c'est pourquoi on passe par elle plutôt
 * que par le formulaire seul. Elle crée le compte, lui ouvre une `Authentication` si
 * l'adresse est neuve, puis la participation avec `registeredBy2` sur le porteur, un
 * `code` et un `voucherCode` — c'est-à-dire SON BADGE. Écrire les mêmes colonnes à la
 * main en oublierait une, et l'oubli ne se verrait qu'à la porte.
 *
 * ⚠️ L'ADRESSE EST FACULTATIVE. Sans elle, la personne existe sans compte : elle ne
 * recevra rien par e-mail et ne pourra pas se connecter, mais elle a son badge. C'est un
 * usage courant — un conjoint, un chauffeur — et le produit l'accepte tel quel.
 *
 * ⚠️ UN COMPTE EXISTANT EST RÉUTILISÉ, PAS ÉCRASÉ. Si l'adresse correspond à quelqu'un
 * du domaine, la route rend ce compte-là avec `canUpdate: false` : ses nom et prénom
 * sont ceux qu'il a déjà, et les réécrire par-dessus renommerait une personne réelle
 * depuis le formulaire d'un tiers. `isAlreadyRegistered` dit, lui, que quelqu'un
 * d'autre l'avait déjà inscrit à cet événement.
 */
export type Addition = {
  id: number;
  /** Ses champs peuvent être complétés : c'est un compte NEUF. */
  modifiable: boolean;
  /** Quelqu'un l'avait déjà inscrit à cet événement. */
  alreadyRegistered: boolean;
};

/**
 * ⚠️ SA PROPRE ADRESSE : LA ROUTE REFUSE (400 `COMPANION_IS_YOU`) SANS RIEN ÉCRIRE. Elle
 * retrouvait le compte du porteur lui-même et en faisait son propre accompagnant :
 * ses étapes s'ouvraient sur les choix du porteur, puis il disparaissait de la liste
 * (filtrée sur `bearer`). L'écran reconnaît ce refus pour le dire en clair.
 */
export function isOwnEmailRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 400 &&
    error.message === "COMPANION_IS_YOU"
  );
}

export async function addCompanion(
  domainId: number,
  eventId: number,
  bearer: number,
  person: { firstName: string; lastName: string; email?: string },
): Promise<Addition> {
  const answer = await apiFetch<{ users?: ReadonlyArray<Record<string, unknown>> }>(
    `/domains/${domainId}/events/${eventId}/companions?userId=${bearer}`,
    { method: "POST", body: { users: [person] } },
  );

  const cree = answer?.users?.[0];
  if (typeof cree?.id !== "number") {
    throw new Error("L'accompagnant n'a pas pu être inscrit.");
  }
  return {
    id: cree.id,
    modifiable: cree.canUpdate !== false,
    alreadyRegistered: cree.isAlreadyRegistered === true,
  };
}

/**
 * LE RETIRE DE L'ÉVÉNEMENT.
 *
 * ⚠️ ELLE NE SUPPRIME PAS LA PERSONNE, elle défait sa participation — le compte lui
 * survit, et c'est ce qu'on veut : il peut être celui de quelqu'un qui existait avant
 * qu'on l'inscrive ici.
 */
export async function removeCompanion(
  domainId: number,
  eventId: number,
  userId: number,
): Promise<void> {
  await apiFetch(
    `/domains/${domainId}/events/${eventId}/companions/${userId}`,
    { method: "DELETE" },
  );
}

/**
 * WHAT THIS TAB REMEMBERS OF ONE COMPANION, FORGOTTEN.
 *
 * ⚠️ « RETIRER » DESTROYS THEIR PARTICIPATION (api-v1 `removeCompanion`): their days,
 * their slots and the company they came under are gone server side. The reads of their
 * steps, cached for good (`staleTime: Infinity`), were not: picked again from the
 * coworkers list, or added again under the same address, the person reopened on the
 * participation that no longer exists. Measured: their old company shown as chosen,
 * hence nothing written, and their new participation left without a company.
 *
 * Forgotten whenever someone comes (back) into the list, picked or added: their steps
 * then read the server again, whoever removed them, here or in another tab. Only THEIR
 * reads: every key here ends with their id, and a companion is never the bearer
 * (`COMPANION_IS_YOU`), whose own reads stay.
 */
export function forgetCompanionReads(
  client: QueryClient,
  { domainId, eventId, userId }: { domainId: number; eventId: number; userId: number },
): void {
  client.removeQueries({
    predicate: ({ queryKey: key }) =>
      /* ["participation" | "form-answers" | "articles-retenus" | "conferences", d, e, id] */
      (key[1] === domainId && key[2] === eventId && key[3] === userId) ||
      /* ["companies", d, id] */
      (key[0] === "companies" && key[1] === domainId && key[2] === userId),
  });
}

/**
 * SES JOURS DE PRÉSENCE.
 *
 * ⚠️ MÊME ORDRE QUE POUR LE PORTEUR, ET POUR LA MÊME RAISON : les jours retenus
 * d'abord, les autres ensuite. Écrire un jour déclenche `updateRegisteredPresent` côté
 * serveur, qui a une branche DÉTRUISANT puis recréant la participation quand le dernier
 * jour retenu tombe. Retirer avant d'ajouter la déclenche pour rien, au milieu de
 * l'écriture. Voir `funnel/participant.ts`, où la règle est établie.
 *
 * ⚠️ ET L'UN APRÈS L'AUTRE. Deux écritures en vol relisent puis réécrivent la même ligne
 * de participation : elles se marchent dessus.
 */
export async function writeDays(
  domainId: number,
  eventId: number,
  userId: number,
  selected: ReadonlyArray<number>,
  all: ReadonlyArray<number>,
): Promise<void> {
  const order = [
    ...all.filter((id) => selected.includes(id)),
    ...all.filter((id) => !selected.includes(id)),
  ];

  for (const dayId of order) {
    const registered = selected.includes(dayId);
    await apiFetch(
      `/domains/${domainId}/events/${eventId}/days/${dayId}/users/${userId}`,
      {
        method: "PUT",
        body: {
          registered: registered,
          registeredAt: registered ? new Date().toISOString() : null,
        },
      },
    );
  }
}

/**
 * CE QUE L'ACCOMPAGNANT REPREND DU PORTEUR — son profil, ses jours, sa commande.
 *
 * ⚠️ LE BROUILLON D'ABORD, L'ENREGISTRÉ ENSUITE. À la PREMIÈRE inscription, les choix du
 * porteur ne vivent encore que dans l'état de navigation du tunnel : la confirmation
 * les écrit, et elle vient APRÈS l'étape accompagnants. Ne lire que le serveur donnait
 * donc à l'accompagnant TOUS les jours de l'événement, aucun profil, et une billetterie
 * vide (bug #200). Le brouillon est aussi la vérité quand on REVIENT corriger : c'est ce
 * que la confirmation s'apprête à écrire par-dessus l'enregistré.
 *
 * ⚠️ LES JOURS PASSENT PAR `selectedDaysOf`, la règle même qui écrira ceux du porteur :
 * l'accompagnant vient quand il vient, ni plus ni moins, et une liste fantaisiste de
 * `history.state` est filtrée contre les jours réels.
 *
 * ⚠️ LA COMMANDE : `undefined` VEUT DIRE « À LIRE SUR LE SERVEUR ». Un brouillon sans
 * billetterie ne dit rien de ce que le porteur a déjà retenu ; un brouillon VIDE, lui,
 * est une réponse (« je n'ai rien pris ») et ne doit pas ressusciter la commande d'avant.
 */
export function carrierHeritage({
  answers,
  recorded,
  all,
}: {
  /** Ce que le porteur vient de répondre dans ce parcours, pas encore écrit. */
  answers: Answers;
  /** Ce que le serveur détient pour lui. */
  recorded: Pick<Recorded, "typoId" | "days"> | undefined;
  /** Tous les jours de l'événement. */
  all: ReadonlyArray<number>;
}): { typoId?: number; days: number[]; articles?: Selection } {
  const typoId =
    typeof answers.typoId === "number"
      ? answers.typoId
      : typeof recorded?.typoId === "number"
        ? recorded.typoId
        : undefined;
  return {
    ...(typoId !== undefined ? { typoId } : {}),
    days: selectedDaysOf({
      coming: true,
      ...(answers.days ? { chosen: answers.days } : {}),
      ...(recorded?.days ? { saved: recorded.days } : {}),
      all,
    }),
    ...(answers.articles ? { articles: answers.articles } : {}),
  };
}

/**
 * LUI DONNE LA TYPOLOGIE DU PORTEUR — sur sa seule participation.
 *
 *   PUT /domains/:d/events/:e/users/:accompagnant   api-v1   { typoId }
 *
 * ⚠️ POUR LE CAS OÙ LE FORMULAIRE NE L'ÉCRIT PAS. À la création, la typologie part avec
 * les réponses du formulaire (`saveAnswers`, base `UE`). Mais ces réponses ne partent
 * que sur un compte NEUF et un formulaire non vide : une adresse déjà connue
 * (`canUpdate: false`) ou un formulaire sans champ laissaient l'accompagnant SANS
 * typologie, alors que la V1 la recopie toujours (`screens/steps/Informations.jsx`).
 *
 * ⚠️ LE CORPS NE PORTE QUE `typoId`. La route n'écrit que les clés reçues : ni le nom,
 * ni les réponses, ni les jours de la personne ne bougent. La règle du panier au
 * changement de typologie ne s'applique pas à un accompagnant (`registeredBy2` posé,
 * voir `api-v1/api/helpers/cartOnTypoChange.js`).
 */
export async function writeCompanionTypo(
  domainId: number,
  eventId: number,
  companionId: number,
  typoId: number,
): Promise<void> {
  await apiFetch(`/domains/${domainId}/events/${eventId}/users/${companionId}`, {
    method: "PUT",
    body: { typoId },
  });
}

/** La commande reprise contient-elle VRAIMENT un article ? Des jours vides ne comptent pas. */
export function hasArticles(selection: Selection | undefined): boolean {
  return (
    !!selection && Object.values(selection).some((list) => list.length > 0)
  );
}
